/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

// React Dependencies
import React, { useEffect, useState, useRef } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { Paper } from '@material-ui/core';
import { Pagination, Skeleton } from '@material-ui/lab';
import { Add, Folder } from '@material-ui/icons';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

// Internal Dependencies
import MText from '../../../components/Common/MText';
import { containerHeaderTextStyle } from '../../../styles/common';
import Colours from '../../../styles/colours';
import MButton from '../../../components/MButton';
import ApiService from '../../../services/apiService';
import ProgressiveImage from '../../../components/ProgressiveImage';
import { imageResizeUrl } from '../../../common/convert';
import { IsEmpty } from '../../../common/checks';
import ToolTip from '../../../components/Common/ToolTip';
import CreateEditAlbumModal from './AlbumsComs/CreateEditAlbumModal';
import { Url, Status } from '../../../common/constants';

const MAX_ALBUMS_PER_PAGE = 36;

const Albums = (props) => {
  // List state
  const [albums, setAlbums] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);

  const containerRef = useRef(null);

  const fetchAlbums = async () => {
    setLoading(true);
    try {
      const result = await ApiService.getPageAlbums(props.selectedPage.id, {
        pageNo:   pageNo - 1, // backend is 0-indexed
        pageSize: MAX_ALBUMS_PER_PAGE,
      });
      setAlbums(result.rows || []);
      setTotalCount(result.totalCount || 0);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlbums();
  }, [pageNo]);

  const onPageChange = (e, page) => {
    setPageNo(page);
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ block: 'start' });
    }
  };

  const renderAlbumList = () => (
    <div className="col-12 col-xl-11 my-3">
      <Paper className="px-4 pt-2 pb-4 mb-5">
        <div className="ml-1 mt-2 mb-3 d-flex flex-row justify-content-start align-items-center">
          <MText size={containerHeaderTextStyle.fontSize} bold text="Albums" />

          <ToolTip title="Create Album">
            <MButton
              disableElevation
              style={{ border: '1px solid black', borderRadius: 99 }}
              textSize={containerHeaderTextStyle.actionBtn.textSize}
              text="Create"
              type="button"
              className="ml-3 px-4"
              onClick={() => {
                setEditingAlbum(null);
                setIsCreateModalOpen(true);
              }}
              icon={() => <Add style={{ fontSize: 11, marginRight: 5 }} />}
            />
          </ToolTip>
        </div>

        <div className="row" style={{ rowGap: 24 }}>
          {loading ? (
            new Array(MAX_ALBUMS_PER_PAGE).fill(0).map((_, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`skeleton-${idx}`} className="col-6 col-md-3 px-2">
                <div style={{ width: '100%', aspectRatio: 1 }}>
                  <Skeleton variant="rect" style={{ width: '100%', height: '100%' }} />
                </div>
              </div>
            ))
          ) : (
            albums.map((album) => {
              let statusColor = Colours.Black;
              if (album.status === Status.Live) {
                statusColor = Colours.Green;
              } else if (album.status === Status.Rejected) {
                statusColor = Colours.Red;
              } else if (album.status === Status.Draft) {
                statusColor = Colours.Gray1;
              }

              return (
                <div key={album.id} className="col-6 col-md-3" style={{ paddingRight: 12, paddingLeft: 12 }}>
                  <div
                    className="d-flex flex-column align-items-center justify-content-center position-relative"
                    style={{
                      width:           '100%',
                      cursor:          'pointer',
                      backgroundColor: Colours.LightGray,
                      borderRadius:    8,
                      border:          `1px solid ${Colours.LightGray2}`,
                      overflow:        'hidden',
                    }}
                    onClick={() => {
                      const detailsUrl = Url.AlbumDetails
                        .replace(':pageId', props.selectedPage.id)
                        .replace(':albumId', album.id);
                      if (containerRef.current) {
                        containerRef.current.scrollIntoView({ block: 'start' });
                      }
                      props.history.push(detailsUrl);
                    }}
                  >
                    {!IsEmpty(album.thumbnails) ? (
                      <div className="w-100 h-100 d-flex flex-wrap">
                        {album.thumbnails.slice(0, 4).map((thumbUrl) => (
                          <div
                            key={thumbUrl}
                            style={{
                              width:  album.thumbnails.length > 1 ? '50%' : '100%',
                              height: album.thumbnails.length > 1 ? '50%' : '100%',
                            }}
                          >
                            <ProgressiveImage
                              image={imageResizeUrl(thumbUrl, 150, 150)}
                              width="100%"
                              height="100%"
                              style={{ aspectRatio: 1, width: '100%', objectFit: 'cover' }}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ aspectRatio: 1, width: '100%' }}
                      >
                        <Folder style={{ fontSize: 70, color: Colours.Gray5 }} />
                      </div>
                    )}

                    <div
                      style={{
                        bottom:          0,
                        width:           '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.85)',
                        padding:         '4px 8px',
                      }}
                    >
                      <MText
                        text={album.name}
                        size={12}
                        medium
                        className="text-center"
                        style={{
                          wordBreak:       'break-word',
                          overflow:        'hidden',
                          display:         '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                        }}
                      />
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-1" style={{ gap: 8 }}>
                        <MText text={`${album.photoCount || 0} photos`} size={11} colour={Colours.Gray8} />
                        <div
                          style={{
                            backgroundColor: statusColor,
                            color:           Colours.White,
                            borderRadius:    12,
                            padding:         '2px 10px',
                            fontSize:        10,
                            fontWeight:      'bold',
                            whiteSpace:      'nowrap',
                          }}
                        >
                          {album.status}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })
          )}
        </div>
      </Paper>

      {totalCount > 0 && (
        <div
          className="position-fixed py-2 pr-5 d-flex flex-row align-items-center"
          style={{ background: Colours.White, bottom: 5, width: '100%', borderRadius: '10px', zIndex: 99 }}
        >
          <Pagination
            color="primary"
            size="large"
            count={Math.ceil(totalCount / MAX_ALBUMS_PER_PAGE)}
            page={pageNo}
            onChange={onPageChange}
            className="float-left"
          />
          <MText className="ml-3" text={`Total count: ${totalCount}`} />
        </div>
      )}
    </div>
  );

  return (
    <div className="container-fluid" ref={containerRef} style={{ minHeight: '100dvh' }}>
      <div className="row d-flex flex-column">
        {renderAlbumList()}
      </div>

      <CreateEditAlbumModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        album={editingAlbum}
        onComplete={(album) => {
          fetchAlbums();
          if (!IsEmpty(album) && IsEmpty(editingAlbum)) {
            const detailsUrl = Url.AlbumDetails
              .replace(':pageId', props.selectedPage.id)
              .replace(':albumId', album.id);
            props.history.push(detailsUrl);
          }
        }}
      />
    </div>
  );
};

Albums.propTypes = {
  selectedPage: PropTypes.shape({
    id:   PropTypes.number.isRequired,
    name: PropTypes.string,
    logo: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    path: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  selectedPage: state.page.selected,
});

export default connect(mapStateToProps, null)(Albums);
